/**
 * Chunk upload orchestration.
 *
 * Per question:
 * 1. Recorder → pushChunk (video ≥256B) into queues.
 * 2. Every 10s (after answer timer starts) → isLastChunk=false, responseDuration matches
 *    chunkDurationSeconds (usually 10; can be less near a duration cap or for tail slices).
 * 3. On Next / time-up → flush queue; final POST isLastChunk=true, responseDuration=full
 *    answer total; earlier chunks use per-slice seconds (remainder / periodic).
 *    Optional tail blob before final → isLastChunk=false, responseDuration=remainder.
 * 4. Each chunk: 1 try + 1 immediate retry (max 2). Last chunk only after prior chunks settle.
 * 5. Done when ≥1 chunk, all success, and lastChunkSent.
 */
import { useCallback, useRef } from "react";

export function useInterviewChunkUploads({ uploadChunk, interviewDataRef }) {
  const stateRef    = useRef({});
  const completedRef = useRef(false);

  // ── helpers ───────────────────────────────────────────────────────────────

  const newUploadSessionId = () => {
    try {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }
    } catch (_) {}
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
      const r = (Math.random() * 16) | 0;
      const v = ch === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const fmtKB    = (b)  => `${Math.round(((b || 0) / 1024) * 10) / 10}KB`;
  // video-only API: ignore blobs smaller than this (empty/corrupt recorder slices).
  const MIN_VIDEO_BYTES = 256;
  const MAX_CHUNK_UPLOAD_ATTEMPTS = 2;
  /** Matches MediaRecorder / RecordRTC timeSlice and flush interval. */
  const CHUNK_SLICE_SEC = 10;
  const FLUSH_INTERVAL_MS = CHUNK_SLICE_SEC * 1000;
  const blobHas  = (b)  => b && typeof b.size === "number" && b.size > 0;
  const videoReady = (b) => blobHas(b) && b.size >= MIN_VIDEO_BYTES;

  const isChunkSettled = (c) =>
    c?.status === "success" ||
    (c?.status === "failed" && (c.attempts || 0) >= MAX_CHUNK_UPLOAD_ATTEMPTS);

  const getSortedChunkKeys = (s) =>
    Object.keys(s?.chunks || {}).sort(
      (a, b) => (s.chunks[a]?.chunkIndex || 0) - (s.chunks[b]?.chunkIndex || 0),
    );

  const toFiniteSeconds = (value, fallback = 0, round = "floor") => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return fallback;
    if (round === "round") return Math.round(n);
    return Math.floor(n);
  };

  const sumChunkDurations = (s) =>
    Object.values(s?.chunks || {}).reduce(
      (acc, ch) => acc + toFiniteSeconds(ch.chunkDurationSeconds, 0),
      0,
    );

  const getQuestionDurationCap = (s) => {
    const expected = toFiniteSeconds(s?.expectedDurationSeconds, 0);
    if (expected > 0) return expected;
    return toFiniteSeconds(s?.durationSeconds, 0);
  };

  /** Elapsed at stop (Next/time-up), set on session before final flush. */
  const getStoppedElapsedSeconds = (s) => {
    const frozen = toFiniteSeconds(s?.durationSeconds, 0);
    if (frozen > 0) return frozen;
    return getAnswerElapsedSeconds(s);
  };

  const touchChunkWallClock = (s) => {
    s.lastChunkWallAt = Date.now();
  };

  /**
   * Periodic flush: slice ≈ answer elapsed minus time already assigned to prior chunks
   * (capped at CHUNK_SLICE_SEC), so a short final slice is not always labeled 10.
   * Still respects question duration cap when set.
   */
  const getPeriodicChunkDurationSeconds = (s) => {
    const accounted = sumChunkDurations(s);
    const elapsed = getAnswerElapsedSeconds(s);
    let slice = Math.min(
      CHUNK_SLICE_SEC,
      Math.max(0, Math.round(elapsed - accounted)),
    );

    const hardCap = getQuestionDurationCap(s);
    touchChunkWallClock(s);

    if (hardCap > 0) {
      const remaining = hardCap - accounted;
      if (remaining <= 0) return 0;
      const capped = Math.min(
        slice > 0 ? slice : CHUNK_SLICE_SEC,
        remaining,
        CHUNK_SLICE_SEC,
      );
      return capped > 0 ? capped : 0;
    }
    if (slice >= 1) return slice;
    return CHUNK_SLICE_SEC;
  };

  const getAnswerElapsedSeconds = (s) => {
    if (!s?.answerStartedAt) return 0;
    const elapsed = (Date.now() - s.answerStartedAt) / 1000;
    const sec = toFiniteSeconds(elapsed, 0);
    const cap = getQuestionDurationCap(s);
    return cap > 0 ? Math.min(sec, cap) : sec;
  };

  /** Total answer time at stop (Next / auto time-up) — timeline for slice math & fallbacks. */
  const getTotalResponseDurationSeconds = (s) => {
    const hardCap = getQuestionDurationCap(s);
    let total = getStoppedElapsedSeconds(s);
    if (total <= 0) total = sumChunkDurations(s);
    if (hardCap > 0) total = Math.min(total, hardCap);
    return total > 0 ? total : 1;
  };

  /**
   * responseDuration per POST:
   * - isLastChunk=true  → full answer duration (total for the question)
   * - otherwise        → this chunk’s slice seconds (chunkDurationSeconds), fallback 10
   */
  const getResponseDurationSeconds = (s, c) => {
    if (c.isLastChunk) {
      return getTotalResponseDurationSeconds(s);
    }
    const sliceDur = toFiniteSeconds(c.chunkDurationSeconds, 0);
    if (sliceDur > 0) {
      return sliceDur;
    }
    return CHUNK_SLICE_SEC;
  };

  const getTotalQuestions = useCallback(() => {
    const t = interviewDataRef?.current?.questions?.length || 0;
    return typeof t === "number" && t > 0 ? t : 0;
  }, [interviewDataRef]);

  /** True only when ≥1 chunk uploaded, all succeeded, and isLastChunk was sent. */
  const isQuestionUploadComplete = (qNum) => {
    const s = stateRef.current[qNum];
    if (!s) return false;
    const chunks = Object.values(s.chunks || {});
    if (chunks.length === 0) return false;
    if (!s.lastChunkSent) return false;
    return chunks.every((c) => c.status === "success");
  };

  // ── flush-timer helpers ───────────────────────────────────────────────────

  const stopFlushTimer = (qNum) => {
    const s = stateRef.current[qNum];
    if (!s) return;
    if (s.flushDebounceId) {
      try { clearTimeout(s.flushDebounceId); } catch (_) {}
      s.flushDebounceId = null;
    }
    if (!s.flushTimer) return;
    try { clearInterval(s.flushTimer); } catch (_) {}
    s.flushTimer = null;
  };

  const stopFlushTimersForOtherQuestions = (qNum) => {
    Object.keys(stateRef.current || {}).forEach((k) => {
      const q = Number(k);
      if (Number.isFinite(q) && q !== qNum) stopFlushTimer(q);
    });
  };

  // ── begin a fresh chunk session for a question ────────────────────────────

  const beginQuestionChunkSession = useCallback((qNum, questionId) => {
    if (typeof qNum !== "number" || qNum <= 0) return null;

    stopFlushTimersForOtherQuestions(qNum);
    stopFlushTimer(qNum);

    const expectedRaw =
      interviewDataRef?.current?.questions?.[qNum - 1]?.expectedDurationSeconds;
    const expectedDurationSeconds =
      typeof expectedRaw === "number" && expectedRaw > 0
        ? Math.round(expectedRaw)
        : null;

    const uploadSessionId = newUploadSessionId();
    stateRef.current[qNum] = {
      uploadSessionId,
      questionId,
      expectedDurationSeconds,
      answerStartedAt : null,
      lastChunkWallAt : null,
      recordingDone   : false,
      audioQueue      : [],
      videoQueue      : [],
      nextIndex       : 0,
      chunks          : {},
      flushTimer      : null,
      lastChunkSent   : false,
      uploadDone      : false,
      uploadPending   : false,
      uploadPromise      : null,
      durationSeconds    : 0,
      pendingLastChunkKey: null,
    };
    console.log(
      `[Chunk] Q${qNum} session started  (questionId: ${questionId}, uploadSessionId: ${uploadSessionId})`,
    );
    return uploadSessionId;
  }, []);

  // ── low-level: upload one chunk descriptor ────────────────────────────────

  const startChunkUpload = useCallback(
    (qNum, key) => {
      const s = stateRef.current[qNum];
      const c = s?.chunks?.[key];
      if (!s || !c) return Promise.resolve(false);

      // Reuse in-flight promise
      if (c.status === "pending" && c.promise) return c.promise;

      if (c.status === "failed" && (c.attempts || 0) >= MAX_CHUNK_UPLOAD_ATTEMPTS) {
        return Promise.resolve(false);
      }

      c.attempts = (c.attempts || 0) + 1;
      c.status   = "pending";

      const startedAt = Date.now();
      const responseDurationSeconds = getResponseDurationSeconds(s, c);
      const lastTag   = c.isLastChunk ? " [LAST]" : "";
      const retryTag  = c.attempts > 1 ? ` (retry ${c.attempts})` : "";
      const durLabel  = c.isLastChunk ? "total" : c.isTailPartial ? "remainder" : "slice";
      console.log(
        `[Chunk] Q${qNum} #${c.chunkIndex}${lastTag} → POST /upload-chunk` +
        `  audio=${fmtKB(c.audioBlob?.size)}, video=${fmtKB(c.videoBlob?.size)}` +
        `, responseDuration=${responseDurationSeconds} (${durLabel})` +
        retryTag,
      );

      if (!videoReady(c.videoBlob)) {
        console.warn(
          `[Chunk] Q${qNum} #${c.chunkIndex} skipped — video missing or below ${MIN_VIDEO_BYTES}B`,
        );
        c.status = "failed";
        return Promise.resolve(false);
      }

      if (!s.uploadSessionId) {
        console.warn(`[Chunk] Q${qNum} #${c.chunkIndex} skipped — missing uploadSessionId`);
        c.status = "failed";
        return Promise.resolve(false);
      }

      const p = Promise.resolve(
        uploadChunk({
          interviewId :
            interviewDataRef?.current?.id ||
            interviewDataRef?.current?.interviewId,
          questionId       : s.questionId,
          uploadSessionId  : s.uploadSessionId,
          chunkIndex       : c.chunkIndex,
          audioBlob        : c.audioBlob,
          videoBlob                 : c.videoBlob,
          isLastChunk               : !!c.isLastChunk,
          responseDurationSeconds,
        }),
      )
        .then(async (ok) => {
          const cur = stateRef.current[qNum]?.chunks?.[key];
          if (!cur) return false;
          const ms = Date.now() - startedAt;
          if (ok) {
            cur.status = "success";
            console.log(`[Chunk] Q${qNum} #${c.chunkIndex}${lastTag} OK  (${ms}ms)`);
            return true;
          }
          cur.status = "failed";
          console.warn(`[Chunk] Q${qNum} #${c.chunkIndex}${lastTag} FAIL  (${ms}ms)`);
          if ((cur.attempts || 0) < MAX_CHUNK_UPLOAD_ATTEMPTS) {
            return startChunkUpload(qNum, key);
          }
          return false;
        })
        .catch(async (err) => {
          const cur = stateRef.current[qNum]?.chunks?.[key];
          if (!cur) return false;
          cur.status = "failed";
          console.warn(`[Chunk] Q${qNum} #${c.chunkIndex} ERROR  ${err?.message || String(err)}`);
          if ((cur.attempts || 0) < MAX_CHUNK_UPLOAD_ATTEMPTS) {
            return startChunkUpload(qNum, key);
          }
          return false;
        });

      c.promise = p;
      return p;
    },
    [uploadChunk, interviewDataRef],
  );

  // ── enqueue a paired blob and fire upload ─────────────────────────────────

  const enqueuePairAndUpload = useCallback(
    (qNum, audioBlob, videoBlob, isLastChunk = false) => {
      const s = stateRef.current[qNum];
      if (!s) return false;

      if (!videoReady(videoBlob)) {
        console.warn(
          `[Chunk] Q${qNum} skip enqueue — no valid video (size=${videoBlob?.size ?? 0})`,
        );
        return false;
      }

      const flagLast = !!isLastChunk;
      const isTailPartial = !flagLast && !!s.recordingDone;

      s.nextIndex = (s.nextIndex || 0) + 1;
      const idx = s.nextIndex;
      const key = String(idx);

      /** Seconds for this blob: timeline left (total − already assigned to other chunks). */
      const sliceFromAnswerTimeline = () => {
        const accountedBefore = sumChunkDurations(s);
        const total = getTotalResponseDurationSeconds(s);
        const v = Math.max(0, Math.round(total - accountedBefore));
        return v > 0 ? v : 1;
      };

      let chunkDurationSeconds;
      if (flagLast || isTailPartial) {
        chunkDurationSeconds = sliceFromAnswerTimeline();
      } else {
        const p = getPeriodicChunkDurationSeconds(s);
        chunkDurationSeconds =
          typeof p === "number" && p > 0 ? p : CHUNK_SLICE_SEC;
      }

      s.chunks[key] = {
        videoBlob,
        audioBlob,
        chunkIndex            : idx,
        chunkDurationSeconds,
        isLastChunk             : flagLast,
        isTailPartial,
        status                  : "idle",
        attempts                : 0,
        promise                 : null,
      };

      if (flagLast) {
        s.pendingLastChunkKey = key;
        console.log(
          `[Chunk] Q${qNum} #${idx} queued as last — upload waits until prior chunks settle`,
        );
        return true;
      }

      startChunkUpload(qNum, key);
      return true;
    },
    [startChunkUpload],
  );

  // ── periodic tick: drain one pair from the queues (never marks isLastChunk) ─
  // video-only API: never POST without a real video blob (no empty padding files).

  const uploadOnePairRef = useRef(() => {});

  const uploadOnePair = useCallback(
    (qNum) => {
      const s = stateRef.current[qNum];
      if (!s || s.recordingDone || s.uploadPending || s.videoQueue.length === 0) return;

      while (s.videoQueue.length > 0) {
        const v = s.videoQueue[0];
        if (!videoReady(v)) {
          s.videoQueue.shift();
          continue;
        }
        s.videoQueue.shift();
        const a = s.audioQueue.length > 0 ? s.audioQueue.shift() : null;
        enqueuePairAndUpload(qNum, blobHas(a) ? a : null, v, false);
        return;
      }
    },
    [enqueuePairAndUpload],
  );
  uploadOnePairRef.current = uploadOnePair;

  const scheduleFlushAttempt = (qNum) => {
    const s = stateRef.current[qNum];
    if (!s || s.recordingDone || s.uploadPending) return;
    if (s.flushDebounceId) {
      try { clearTimeout(s.flushDebounceId); } catch (_) {}
    }
    s.flushDebounceId = setTimeout(() => {
      const cur = stateRef.current[qNum];
      if (cur) cur.flushDebounceId = null;
      try { uploadOnePairRef.current?.(qNum); } catch (_) {}
    }, 50);
  };

  // ── answer timer started (TTS / pre-countdown finished) ───────────────────

  const markAnswerTimerStarted = useCallback((qNum) => {
    const s = stateRef.current[qNum];
    if (!s || s.answerStartedAt) return;
    const now = Date.now();
    s.answerStartedAt = now;
    s.lastChunkWallAt = now;
    console.log(`[Chunk] Q${qNum} answer timer started — duration tracking from ${now}`);
  }, []);

  const getQuestionAnswerElapsedSeconds = useCallback((qNum) => {
    const s = stateRef.current[qNum];
    return getAnswerElapsedSeconds(s);
  }, []);

  // ── start 10-second periodic flush timer ──────────────────────────────────

  const startChunkFlushTimer = useCallback(
    (qNum) => {
      const s = stateRef.current[qNum];
      if (!s || s.flushTimer) return;

      stopFlushTimer(qNum);

      s.flushTimer = setInterval(() => {
        if (completedRef.current) { stopFlushTimer(qNum); return; }
        try { uploadOnePair(qNum); } catch (_) {}
      }, FLUSH_INTERVAL_MS);
    },
    [uploadOnePair],
  );

  // ── push a raw blob from recorder ondataavailable ─────────────────────────

  const pushChunk = useCallback((qNum, kind, blob) => {
    if (typeof qNum !== "number" || qNum <= 0) return;
    if (!blob || (typeof blob.size === "number" && blob.size === 0)) return;
    if (kind === "video" && !videoReady(blob)) {
      console.warn(`[Chunk] Q${qNum} dropped tiny video slice (${blob.size}B)`);
      return;
    }
    const s = stateRef.current[qNum];
    if (!s) return;
    // Allow final ondataavailable blobs during startTrackedUpload's drain window.
    if (s.uploadDone) return;
    if (kind === "video") {
      s.videoQueue.push(blob);
      scheduleFlushAttempt(qNum);
    } else if (kind === "audio") {
      s.audioQueue.push(blob);
    }
    console.log(
      `[Chunk] Q${qNum} recorder emitted ${kind}=${fmtKB(blob.size)}` +
      `  queue: audio=${s.audioQueue.length}, video=${s.videoQueue.length}`,
    );
  }, []);

  // Re-POST the highest-index chunk with isLastChunk=true (same chunkIndex — not N+1).
  const markLastUploadedChunk = useCallback(
    (qNum) => {
      const s = stateRef.current[qNum];
      if (!s) return false;

      const keys = Object.keys(s.chunks || {});
      if (!keys.length) return false;

      let lastKey = keys[0];
      let lastIdx = s.chunks[lastKey]?.chunkIndex || 0;
      for (const key of keys) {
        const idx = s.chunks[key]?.chunkIndex || 0;
        if (idx >= lastIdx) {
          lastIdx = idx;
          lastKey = key;
        }
      }

      const c = s.chunks[lastKey];
      if (!c) return false;

      if (c.isLastChunk) {
        s.lastChunkSent = true;
        console.log(`[Chunk] Q${qNum} chunk #${c.chunkIndex} already has isLastChunk=true`);
        return lastKey;
      }

      if (c.status !== "success") {
        console.warn(
          `[Chunk] Q${qNum} cannot finalize chunk #${c.chunkIndex} — prior upload did not succeed`,
        );
        return false;
      }

      if (!videoReady(c.videoBlob)) {
        console.warn(
          `[Chunk] Q${qNum} cannot set isLastChunk on chunk #${c.chunkIndex} — no video data`,
        );
        return false;
      }

      // Re-flag for one upload via ensureChunkSuccess (avoid startChunkUpload + ensure double-call).
      c.isLastChunk = true;
      // Was likely sent as a periodic row (responseDuration 10); fix slice vs frozen total.
      {
        const prev = toFiniteSeconds(c.chunkDurationSeconds, 0);
        const sumAll = sumChunkDurations(s);
        const accountedWithout = Math.max(0, sumAll - prev);
        const total = getTotalResponseDurationSeconds(s);
        let v = Math.max(0, Math.round(total - accountedWithout));
        if (v < 1) v = 1;
        c.chunkDurationSeconds = Math.min(v, total);
      }
      c.status = "idle";
      c.promise = null;
      c.attempts = 0;
      s.pendingLastChunkKey = lastKey;

      console.log(
        `[Chunk] Q${qNum} chunk #${c.chunkIndex} marked isLastChunk=true (upload deferred)`,
      );
      return lastKey;
    },
    [],
  );

  // ── drain remaining queue; last slice is isLastChunk=true + videoChunk ──────

  const flushAllRemaining = useCallback(
    (qNum) => {
      const s = stateRef.current[qNum];
      if (!s) return;

      // Pair queued slices; only keep rows that have uploadable video (never empty video files).
      const pendingVideo = [...s.videoQueue];
      const pendingAudio = [...s.audioQueue];
      s.videoQueue.length = 0;
      s.audioQueue.length = 0;

      const pairs = [];
      const n = Math.max(pendingVideo.length, pendingAudio.length);
      for (let i = 0; i < n; i += 1) {
        const v = pendingVideo[i];
        const a = pendingAudio[i];
        if (!videoReady(v)) {
          if (blobHas(a)) s.audioQueue.push(a);
          continue;
        }
        pairs.push({ audio: blobHas(a) ? a : null, video: v });
      }

      if (pairs.length > 0) {
        pairs.forEach((p, i) => {
          const isLast = i === pairs.length - 1;
          enqueuePairAndUpload(qNum, p.audio, p.video, isLast);
        });
        return;
      }

      // Re-queue any audio that was waiting for a video slice
      if (s.audioQueue.length === 0 && pendingAudio.some(blobHas)) {
        pendingAudio.filter(blobHas).forEach((a) => s.audioQueue.push(a));
      }

      // Case B/C — queue already drained by the periodic timer
      const keys = Object.keys(s.chunks || {});
      if (!keys.length) {
        console.warn(
          `[Chunk] Q${qNum} no chunks and no queued data — skipping empty isLastChunk upload`,
        );
        return;
      }

    },
    [enqueuePairAndUpload],
  );

  // ── retry a chunk key: initial attempt + one immediate retry (max 2) ───────

  const ensureChunkSuccess = useCallback(
    async (qNum, key) => {
      const c0 = stateRef.current[qNum]?.chunks?.[key];
      if (!c0) return false;

      if (c0.status === "pending" && c0.promise) {
        const ok = await c0.promise;
        if (ok) return true;
      }
      if (stateRef.current[qNum]?.chunks?.[key]?.status === "success") return true;
      if (isChunkSettled(c0)) return false;

      while (true) {
        const c = stateRef.current[qNum]?.chunks?.[key];
        if (!c) return false;
        if (c.status === "success") return true;
        if (isChunkSettled(c)) return false;
        if (c.status === "pending" && c.promise) {
          const ok = await c.promise;
          if (ok) return true;
          continue;
        }
        const ok = await startChunkUpload(qNum, key);
        if (ok) return true;
      }
    },
    [startChunkUpload],
  );

  const settleNonLastChunks = useCallback(
    async (qNum) => {
      const s = stateRef.current[qNum];
      if (!s) return;
      for (const key of getSortedChunkKeys(s)) {
        const c = s.chunks[key];
        if (!c || c.isLastChunk) continue;
        await ensureChunkSuccess(qNum, key);
      }
    },
    [ensureChunkSuccess],
  );

  const uploadDeferredLastChunk = useCallback(
    async (qNum) => {
      const s = stateRef.current[qNum];
      if (!s) return false;

      const nonLast = Object.values(s.chunks || {}).filter((c) => !c.isLastChunk);
      const priorOk = nonLast.every((c) => isChunkSettled(c));
      if (!priorOk) {
        console.warn(`[Chunk] Q${qNum} cannot send last chunk — prior chunks not settled`);
        return false;
      }

      let lastKey = s.pendingLastChunkKey;
      if (!lastKey) {
        const entry = Object.entries(s.chunks || {}).find(([, ch]) => ch.isLastChunk);
        if (entry) lastKey = entry[0];
      }
      if (!lastKey) lastKey = markLastUploadedChunk(qNum);
      if (!lastKey) return false;

      s.pendingLastChunkKey = null;
      const ok = await ensureChunkSuccess(qNum, lastKey);
      if (ok) s.lastChunkSent = true;
      return ok;
    },
    [ensureChunkSuccess, markLastUploadedChunk],
  );

  // ── main entry: stop timer → wait for final blob → flush → wait uploads ───
  //
  // BUG FIX (was in previous version): keys were snapshotted BEFORE
  // flushAllRemaining had a chance to enqueue the last chunk, so the
  // ensureChunkSuccess loop would miss it.
  // FIX: snapshot keys AFTER flushAllRemaining returns.

  const startTrackedUpload = useCallback(
    async (payload) => {
      const qNum = payload?.questionNumber;
      if (typeof qNum !== "number" || qNum <= 0) return false;
      const s = stateRef.current[qNum];
      if (!s) return false;

      // Idempotency
      if (s.uploadDone) return true;
      if (s.uploadPending && s.uploadPromise) return s.uploadPromise;

      s.recordingDone   = true;
      s.durationSeconds = toFiniteSeconds(payload?.durationSeconds, 0);
      s.uploadPending   = true;
      try { stopFlushTimer(qNum); } catch (_) {}

      const run = async () => {
        // Timer already stopped above (sync) so the 10s interval cannot race with flush.

        // 2. Wait 300 ms for the recorder to emit its final ondataavailable blob
        //    into the queue via pushChunk() before we drain it.
        await new Promise((r) => setTimeout(r, 600));

        // 3. Drain remaining queue blobs and mark the last one isLastChunk=true
        console.log(`[Chunk] Q${qNum} answer ended — flushing remaining queue`);
        try { flushAllRemaining(qNum); } catch (e) {
          console.warn(`[Chunk] Q${qNum} flush error — ${e?.message || String(e)}`);
        }

        // 4. Non-last chunks first (each gets one immediate retry), then last chunk.
        await settleNonLastChunks(qNum);
        if (!stateRef.current[qNum]?.lastChunkSent) {
          await uploadDeferredLastChunk(qNum);
        }

        const allOk = isQuestionUploadComplete(qNum);

        if (allOk) {
          console.log(`[Chunk] Q${qNum} all chunks uploaded successfully ✓`);
        } else {
          const failed = Object.values(stateRef.current[qNum]?.chunks || {})
            .filter((c) => c.status !== "success")
            .map((c) => c.chunkIndex);
          console.warn(`[Chunk] Q${qNum} some chunks failed: [${failed.join(", ")}]`);
        }

        const cur = stateRef.current[qNum];
        if (cur) {
          cur.uploadDone    = allOk;
          cur.uploadPending = false;
        }
        return allOk;
      };

      s.uploadPromise = run();
      return s.uploadPromise;
    },
    [flushAllRemaining, settleNonLastChunks, uploadDeferredLastChunk],
  );

  // ── ensure every question's uploads are settled before final submit ────────

  const ensureAllUploadsSettled = useCallback(async () => {
    const totalQ = getTotalQuestions();
    if (!totalQ) return { ok: true, totalQ: 0, failedQuestions: [], missingQuestions: [] };

    const CONCURRENCY = 3;

    const settleOne = async (q) => {
      const s = stateRef.current[q];
      if (!s) return { q, kind: "missing" };

      if (s.uploadDone) return null;

      if (s.uploadPending && s.uploadPromise) {
        const ok = await s.uploadPromise;
        return ok ? null : { q, kind: "failed" };
      }

      await settleNonLastChunks(q);
      if (!stateRef.current[q]?.lastChunkSent) {
        await uploadDeferredLastChunk(q);
      }

      const allOk = isQuestionUploadComplete(q);
      if (allOk) {
        const cur = stateRef.current[q];
        if (cur) cur.uploadDone = true;
      }
      return allOk ? null : { q, kind: "failed" };
    };

    const queue   = Array.from({ length: totalQ }, (_, i) => i + 1);
    const results = [];
    const workers = Array.from(
      { length: Math.min(CONCURRENCY, queue.length) },
      async () => {
        while (queue.length) {
          const q = queue.shift();
          if (typeof q !== "number") return;
          const r = await settleOne(q);
          if (r) results.push(r);
        }
      },
    );

    await Promise.all(workers);

    const failedQuestions  = results.filter((r) => r.kind === "failed").map((r) => r.q);
    const missingQuestions = results.filter((r) => r.kind === "missing").map((r) => r.q);
    return {
      ok: failedQuestions.length === 0 && missingQuestions.length === 0,
      totalQ,
      failedQuestions,
      missingQuestions,
    };
  }, [getTotalQuestions, settleNonLastChunks, uploadDeferredLastChunk]);

  // ── quick check ───────────────────────────────────────────────────────────

  const areAllUploadsSuccessful = useCallback(() => {
    const totalQ = getTotalQuestions();
    if (!totalQ) return true;
    for (let q = 1; q <= totalQ; q++) {
      if (!isQuestionUploadComplete(q)) return false;
    }
    return true;
  }, [getTotalQuestions]);

  // ── cancel all background retries on completion ───────────────────────────

  const markCompletedCancelRetries = useCallback(() => {
    completedRef.current = true;
    try {
      Object.keys(stateRef.current || {}).forEach((k) => {
        const q = Number(k);
        if (Number.isFinite(q)) stopFlushTimer(q);
      });
    } catch (_) {}
  }, []);

  /** Stop periodic flush immediately when the candidate ends an answer (Next / time-up). */
  const endAnswerRecording = useCallback((qNum) => {
    const s = stateRef.current[qNum];
    if (!s) return;
    s.recordingDone = true;
    try { stopFlushTimer(qNum); } catch (_) {}
  }, []);

  return {
    startTrackedUpload,
    ensureAllUploadsSettled,
    areAllUploadsSuccessful,
    beginQuestionChunkSession,
    pushChunk,
    startChunkFlushTimer,
    markAnswerTimerStarted,
    getQuestionAnswerElapsedSeconds,
    endAnswerRecording,
    markCompletedCancelRetries,
  };
}